import type { ChatProvider } from '@xsai-ext/shared-providers'
import type { CommonContentPart, CompletionToolCall, Message } from '@xsai/shared-chat'

import type { ChatToolContext } from '../tools/chat-actions'
import type { ToolLifecycle } from '../tools/tool-lifecycle'
import type { AnthropicStreamResult } from './anthropic-native-stream'
import type { EndpointKind } from './endpoint-kind'
import type { CompatStreamResult } from './openai-compat-stream'

import { readableStreamToAsyncIterator } from '@moeru/std'
import { listModels } from '@xsai/model'
import { streamText } from '@xsai/stream-text'
import { defineStore } from 'pinia'
import { ref, toRaw } from 'vue'

import { debug, mcp } from '../tools'
import { buildChatActionTools, getTerminalChatActionToolNames } from '../tools/chat-actions'
import { configureMCPTool } from '../tools/mcp-configure'
import { assembleMCPToolset } from '../tools/mcp-meta'
import { ACTION_TOOL_NAMES, resolveToolLifecycle } from '../tools/tool-lifecycle'
import { getClaudeHeaders, isAnthropicOfficialHost, isBaseUrlIncompatible, isClaudeModel, markBaseUrlAsIncompatible } from '../utils/claude-caching'
import { moveNonStableSystemLayersToLatestUser } from '../utils/prompt-cache-layout'
import { readAnthropicStream, runAnthropicToolLoop } from './anthropic-native-stream'
import { compressLargeDataUrlsInMessages, convertContentArrayToAnthropic, embedQuotePrefix } from './chat-helpers'
import { resolveEndpointKind } from './endpoint-kind'
import { executeGeminiStream, messagesToGeminiContents, runGeminiToolLoop, toolsToGeminiDeclarations } from './gemini-native-stream'
import { readDeviceIdSync } from './license'
import { logCacheUsage, normalizeCompatibleCacheUsage } from './llm-cache-diagnostics'
import { stripDisplayOnlyFields } from './llm-helpers'
import { getAnthropicThinkingConfig, getGeminiThinkingConfig, getOpenAICompatibleReasoningConfig, getOpenAIResponsesReasoningConfig, isAnthropicForcedToolChoiceUnsupportedModel } from './llm-thinking'
import { executeOpenAICompatStream, runCompatToolLoop } from './openai-compat-stream'
import { executeResponsesStream, messagesToResponsesInput, runResponsesToolLoop, toolsToResponsesTools } from './openai-responses-stream'
import { useSettings } from './settings'
import { forwardStructuredEvents } from './structured-output'
import { filterToolsByStructuredAllowlist, resolveStructuredToolAllowlist } from './structured-tool-policy'

// 🔧 辅助函数：深度移除 Vue 响应式代理
function deepToRaw<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj
  }

  const raw = toRaw(obj)

  if (Array.isArray(raw)) {
    return raw.map(item => deepToRaw(item)) as T
  }

  if (typeof raw === 'object') {
    const result: any = {}
    for (const key in raw) {
      if (Object.prototype.hasOwnProperty.call(raw, key)) {
        result[key] = deepToRaw((raw as any)[key])
      }
    }
    return result
  }

  return raw
}

function simulatedImagesText(message: any): string | undefined {
  if (!Array.isArray(message?.simulatedImages) || message.simulatedImages.length === 0)
    return undefined
  const countLabel = message.simulatedImages.length > 1 ? `${message.simulatedImages.length}张` : '一张'
  return `[发送了${countLabel}模拟图片]\n${message.simulatedImages.map((image: any, index: number) => {
    return `图片${index + 1}：${String(image?.description ?? '').trim()}`
  }).join('\n')}`
}

function contentForModel(message: any): unknown {
  return simulatedImagesText(message) ?? message?.content
}

/** 注册工具执行器，并让所有原生终止工具统一通知聊天收尾逻辑。 */
function registerToolExecutor(
  executors: Map<string, (input: unknown, opts: any) => Promise<any>>,
  name: string,
  execute: (input: unknown, opts: any) => Promise<any>,
  isTerminal: boolean,
  onTerminalToolHandled?: () => void,
  onActionToolHandled?: () => void,
  lifecycle?: 'query' | 'action' | 'terminal',
): void {
  const effectiveLifecycle = lifecycle ?? (ACTION_TOOL_NAMES.has(name) ? 'action' : isTerminal ? 'terminal' : 'query')
  executors.set(name, async (input, opts) => {
    const startedAt = performance.now()
    let failed = false
    try {
      const result = await execute(input, opts)
      if (effectiveLifecycle === 'action')
        onActionToolHandled?.()
      else if (effectiveLifecycle === 'terminal')
        onTerminalToolHandled?.()
      return result
    }
    catch (error) {
      failed = true
      throw error
    }
    finally {
      console.debug(`[tool] ${name} lifecycle=${effectiveLifecycle} durationMs=${Math.round(performance.now() - startedAt)} failed=${failed}`)
    }
  })
}

// 🔧 辅助函数：合并多个 AbortSignal（兼容不支持 AbortSignal.any 的浏览器）
function mergeAbortSignals(signal1: AbortSignal, signal2: AbortSignal): AbortSignal {
  const controller = new AbortController()
  const onAbort1 = () => controller.abort(signal1.reason)
  const onAbort2 = () => controller.abort(signal2.reason)
  signal1.addEventListener('abort', onAbort1, { once: true })
  signal2.addEventListener('abort', onAbort2, { once: true })
  // 如果任一 signal 已经被中止，立即传播
  if (signal1.aborted)
    controller.abort(signal1.reason)
  if (signal2.aborted)
    controller.abort(signal2.reason)
  return controller.signal
}

// 开发环境代理 fetch：把跨域请求路由到 Vite llmCorsProxy 中间件，避免 CORS 拦截
function devProxyFetch(input: URL | RequestInfo, init?: RequestInit): Promise<Response> {
  if (import.meta.env.DEV) {
    const originalUrl = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url
    try {
      const parsed = new URL(originalUrl)
      if (parsed.origin !== globalThis.location.origin) {
        const proxyUrl = `/api/llm-cors-proxy${parsed.pathname}${parsed.search}`
        return globalThis.fetch(proxyUrl, {
          ...init,
          headers: {
            ...(init?.headers as Record<string, string> | undefined),
            'x-llm-proxy-target': parsed.origin,
          },
        })
      }
    }
    catch {
      // URL 解析失败，直接发出
    }
  }
  return globalThis.fetch(input, init)
}

export type StreamEvent
  = | { type: 'text-delta', text: string }
    | { type: 'reasoning-delta', text: string }
    | ({ type: 'finish' } & any)
    | ({ type: 'tool-call' } & CompletionToolCall)
    // 工具调用开始（args 尚未完全返回）。两条路径都会发射：
    //   - Anthropic 原生：由 content_block_start(tool_use) 触发
    //   - xsAI 兼容：由首次出现该 toolCallId 的事件（tool-call-delta / tool-call）合成
    | { type: 'tool-call-streaming-start', toolCallId: string, toolName: string }
    | { type: 'tool-result', toolCallId: string, result?: string | CommonContentPart[] }
    | { type: 'error', error: any }

export interface StreamOptions {
  headers?: Record<string, string>
  onStreamEvent?: (event: StreamEvent) => void | Promise<void>
  toolsCompatibility?: Map<string, boolean>
  supportsTools?: boolean
  abortSignal?: AbortSignal
  // 用户/设备维度隔离 ID，用于 Anthropic metadata.user_id，避免多人共 apiKey 串缓存
  cacheScopeId?: string
  // 调用方自定义工具列表（格式需与 @xsai/tool 的 rawTool 输出一致）。
  // [] = 显式禁用工具；undefined = 回落到默认的 mcp() + debug()
  tools?: unknown[]
  // 工具调用最大步数；默认单轮最多 20 次，调用方可显式覆盖。
  maxSteps?: number
  // 聊天动作工具上下文（包含 terminal/action/query）
  // 传入后自动合并进工具列表；terminal 执行后不回送 tool_result，不触发第二轮 LLM
  chatToolContext?: ChatToolContext
}

function getStructuredToolAllowlist(tools: unknown[] | undefined, options: StreamOptions | undefined): Set<string> | undefined {
  const context = options?.chatToolContext
  if (!context)
    return undefined
  const availableToolNames = (tools ?? []).flatMap((tool) => {
    const name = (toRaw(tool) as { function?: { name?: unknown } })?.function?.name
    return typeof name === 'string' ? [name] : []
  })
  return resolveStructuredToolAllowlist(availableToolNames, {
    taskKind: context.taskKind,
    isGroupChat: context.isGroupChat,
    callState: context.callState,
  })
}

function streamOptionsToolsCompatibilityOk(model: string, chatProvider: ChatProvider, _: Message[], options?: StreamOptions, toolsCompatibility: Map<string, boolean> = new Map()): boolean {
  // 🔧 安全检查：确保 chatProvider 存在
  if (!chatProvider) {
    return false
  }
  return !!(options?.supportsTools || toolsCompatibility.get(`${chatProvider.chat(model).baseURL}-${model}`))
}

// 🔧 判断是否为可重试的临时错误（503/429/502/500/网络失败等）
function isRetryableError(error: unknown): boolean {
  const msg = String(error)
  return msg.includes('503') || msg.includes('429') || msg.includes('502')
    || msg.includes('Service Unavailable') || msg.includes('Too Many Requests')
    || msg.includes('high demand') || msg.includes('rate limit')
    || msg.includes('Failed to fetch') || msg.includes('NetworkError')
    || msg.includes('fetch failed')
}

// 🔧 重试配置
const RETRY_CONFIG = {
  maxRetries: 2,
  baseDelayMs: 2000, // 首次重试等 2 秒
  maxDelayMs: 8000,
}

function shouldBufferSseStopLine(line: string): boolean {
  if (!line.includes('"finish_reason"') || !line.includes('"stop"'))
    return false

  try {
    const json = JSON.parse(line.slice(6))
    const delta = json?.choices?.[0]?.delta
    const finishReason = json?.choices?.[0]?.finish_reason
    return finishReason === 'stop' && !delta?.content
  }
  catch {
    return false
  }
}

async function sha256Hex(input: string): Promise<string | null> {
  // crypto.subtle 仅在 secure context (HTTPS / localhost / file://) 下可用
  // 纯 HTTP 部署或内网 IP 直连时为 undefined，此时返回 null
  // 表示本次不附带 metadata.user_id，Claude 官方不强制，缓存粒度会退化到 apiKey 维度
  const subtle = globalThis.crypto?.subtle
  if (!subtle)
    return null
  try {
    const data = new TextEncoder().encode(input)
    const buf = await subtle.digest('SHA-256', data)
    return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('')
  }
  catch {
    return null
  }
}

// metadata.user_id 在每次 send 热路径都会用到；同一 (apiKey, scopeId) 的 hash 值不会变，缓存即可
const metadataUserIdCache = new Map<string, string>()
async function resolveMetadataUserId(apiKey: string, scopeId: string): Promise<string | null> {
  if (!apiKey && !scopeId)
    return null
  const key = `${apiKey}|${scopeId}`
  const cached = metadataUserIdCache.get(key)
  if (cached)
    return cached
  const hash = await sha256Hex(key)
  if (!hash)
    return null
  const value = `meowchat-${hash.slice(0, 55)}` // prefix(9) + hash(55) = 64，不超过 Anthropic prompt_cache_key 上限
  metadataUserIdCache.set(key, value)
  return value
}

// 用户/设备维度隔离 ID 的上游字段最大长度（Anthropic metadata.user_id / OpenAI user 等约定上限）
const SCOPE_ID_MAX_LEN = 128

// 缺失 subtle crypto 时的一次性告警，避免多租户环境下静默退化为共享缓存桶
let warnedMissingCryptoSubtle = false
async function resolveCacheScope(apiKey: string, explicitScopeId?: string): Promise<string | null> {
  const provided = typeof explicitScopeId === 'string' && explicitScopeId.length <= SCOPE_ID_MAX_LEN
    ? explicitScopeId
    : ''
  const scopeId = provided || readDeviceIdSync()
  const userId = await resolveMetadataUserId(apiKey, scopeId)
  if (!userId && !warnedMissingCryptoSubtle) {
    warnedMissingCryptoSubtle = true
    console.warn('[llm] crypto.subtle 不可用，LLM 请求将缺少用户/设备隔离字段，共享 apiKey 场景的缓存会退化到 key 维度')
  }
  return userId
}

// Anthropic 原生 Messages API 流式请求（仅 Claude 模型使用，绕过 OpenAI 兼容层以支持 prompt caching）
async function streamFromAnthropicNative(
  model: string,
  chatProvider: ChatProvider,
  messages: Message[],
  options?: StreamOptions,
): Promise<{ textStream: ReadableStream<string> }> {
  if (!chatProvider)
    throw new Error('streamFromAnthropicNative: chatProvider is undefined')

  const plainOptions = options ? toRaw(options) : undefined
  const extraHeaders = plainOptions?.headers ? deepToRaw(plainOptions.headers) : {}
  const { thinkingLevel } = useSettings()

  // 准备消息：过滤 error 消息 + 深度移除响应式代理 + 引用嵌入
  const rawMessages = deepToRaw(
    messages.filter(msg => (msg.role as string) !== 'error').map((msg) => {
      const rawMsg = toRaw(msg)
      const role = rawMsg.role as string
      const content = embedQuotePrefix(rawMsg, contentForModel(rawMsg) as any)
      return { role, content }
    }),
  )

  // 预压缩 + http→dataURL 标准化（命中现有 LRU 缓存），统一上游图片格式后再做 Anthropic 转换
  await compressLargeDataUrlsInMessages(rawMessages as Array<{ content?: unknown }>)

  // 提取 system → Anthropic system 参数；其余 → messages 数组
  // 关键：把 OpenAI 风格的 image_url part 转换成 Anthropic 原生 image block，
  // 否则官方端点会返回 400，宽松代理会把 image part 当未知字段忽略，AI 看不到图。
  // system 字段只接受 text block，遇到 image_url 直接丢弃（不进入 image 转换）。
  const systemBlocks: any[] = []
  const anthropicMessages: any[] = []
  for (const msg of rawMessages) {
    if (msg.role === 'system') {
      const c = msg.content
      if (typeof c === 'string' && c.trim()) {
        systemBlocks.push({ type: 'text', text: c })
      }
      else if (Array.isArray(c)) {
        for (const part of c) {
          if (typeof part === 'object' && part !== null && (part as { type?: string }).type === 'text')
            systemBlocks.push(part)
          // image_url / image / 其他类型一律不进 system，避免 Anthropic 返回 400
        }
      }
    }
    else {
      const convertedContent = await convertContentArrayToAnthropic(msg.content)
      anthropicMessages.push({ role: msg.role === 'tool' ? 'user' : msg.role, content: convertedContent })
    }
  }

  // Cache only the stable system prefix and let the provider apply its default TTL.
  if (systemBlocks.length >= 1) {
    systemBlocks[0].cache_control = { type: 'ephemeral' }
  }

  // 获取工具列表并转换为 Anthropic 格式
  // 优先使用调用方传入的 tools（插件工作台等自定义工具链），否则回落到默认 mcp+debug
  // [] = 显式禁用，undefined = 走下面的默认链
  let toolsList: unknown[] | undefined
  // MCP 始终按元工具注入（list/load/call/remove），不把服务器真实工具全量塞进上下文
  const mcpToolset = await assembleMCPToolset()
  const userMcpToolsAnthropic = mcpToolset.tools
  // 聊天动作工具（包含终止动作和需要继续对话的查询工具）
  const chatActionTools = plainOptions?.chatToolContext
    ? buildChatActionTools(plainOptions.chatToolContext)
    : []
  const requireStructuredOutput = !!plainOptions?.chatToolContext
  // 终止工具名称集合：执行后不追加 tool_result，不触发第二轮 LLM
  const terminalToolNames = getTerminalChatActionToolNames(chatActionTools)

  if (plainOptions?.tools !== undefined) {
    // [] = 显式禁用所有工具（含 configure_mcp）
    const base = plainOptions.tools.length > 0
      ? [...plainOptions.tools, configureMCPTool, ...userMcpToolsAnthropic]
      : []
    const merged = [...base, ...chatActionTools]
    toolsList = merged.length > 0 ? merged : undefined
  }
  else {
    const toolsOk = streamOptionsToolsCompatibilityOk(model, chatProvider, messages, plainOptions, plainOptions?.toolsCompatibility)
    const builtinTools = toolsOk ? [...await mcp(), ...await debug()] : []
    // configure_mcp 常驻：只要有任何工具（或用户配置了 MCP）就加入
    const allTools = [...builtinTools, ...userMcpToolsAnthropic, ...chatActionTools, configureMCPTool]
    toolsList = (toolsOk || userMcpToolsAnthropic.length > 0 || chatActionTools.length > 0) ? allTools : undefined
    console.debug(`🔧 [Tools] mcpMode=${mcpToolset.mode} mcp=${userMcpToolsAnthropic.length}(servers=${mcpToolset.serverCount},totalTools=${mcpToolset.totalToolCount}) chatAction=${chatActionTools.length} builtin=${builtinTools.length} names=[${userMcpToolsAnthropic.map((t: any) => t.function.name).join(',')}]`)
  }
  const allowedStructuredTools = getStructuredToolAllowlist(toolsList, plainOptions)
  if (allowedStructuredTools) {
    toolsList = filterToolsByStructuredAllowlist(
      (toolsList ?? []) as Array<{ function?: { name?: string } }>,
      allowedStructuredTools,
    )
  }
  const toolExecutors = new Map<string, (input: unknown, opts: any) => Promise<any>>()
  const lifecycleByName = new Map<string, ToolLifecycle>()
  const anthropicTools = toolsList?.map((t: any) => {
    const raw = toRaw(t)
    const lifecycle = resolveToolLifecycle(raw, terminalToolNames)
    lifecycleByName.set(raw.function.name, lifecycle)
    if (raw.execute) {
      registerToolExecutor(
        toolExecutors,
        raw.function.name,
        raw.execute,
        lifecycle === 'terminal',
        plainOptions?.chatToolContext?.onTerminalToolHandled,
        plainOptions?.chatToolContext?.onActionToolHandled,
        lifecycle,
      )
    }
    return { name: raw.function.name, description: raw.function.description, input_schema: raw.function.parameters }
  })
  // 配置
  const chatConfig = deepToRaw(chatProvider.chat(model))
  const baseURL = String(chatConfig.baseURL).replace(/\/+$/, '')
  const messagesUrl = `${baseURL}/messages?beta=true`
  const apiKey = (chatConfig as any).apiKey || ''

  // max_tokens：按用户要求完全不限制。
  // 实测 neko-teaparty / New API 网关（api.nktp.top）不强制 max_tokens，
  // 省略该字段返回 200 并正常完成（官方 Anthropic API 会 400，但此处不走官方）。
  // 因此请求体不再传 max_tokens，输出长度由上游自行决定。
  // 仅旧模型（3.7-sonnet）需要 output-128k beta 才能 >8k 输出，但这些模型已基本退役，
  // 且上游不强制该 beta，故一并去掉按模型计算上限的逻辑。
  const needsLongOutputBeta = false

  const betaFlags = [
    'prompt-caching-2024-07-31',
    'extended-cache-ttl-2025-04-11',
    ...(needsLongOutputBeta ? ['output-128k-2025-02-19'] : []),
  ]

  const requestHeaders: Record<string, string> = {
    'content-type': 'application/json',
    'anthropic-version': '2023-06-01',
    // prompt-caching-2024-07-31: 基础 prompt caching（5分钟 TTL）
    // extended-cache-ttl-2025-04-11: 启用 1h TTL 支持
    // output-128k-2025-02-19: 仅 Sonnet-4 / 3.7-sonnet 启用 ≥8k 输出
    'anthropic-beta': betaFlags.join(','),
    ...(apiKey ? { 'x-api-key': apiKey, 'authorization': `Bearer ${apiKey}` } : {}),
    ...extraHeaders,
  }

  // neko-teaparty 要求请求体带 metadata.user_id 才会应用 cache_control
  // 用 apiKey + cacheScopeId（通常是 deviceId）双维度隔离：
  // - apiKey 维度：不同 apiKey 天然隔离
  // - cacheScopeId 维度：同 apiKey 被多人共用时也能按设备隔离
  const metadataUserId = await resolveCacheScope(apiKey, plainOptions?.cacheScopeId)

  async function executeAnthropicStream(
    body: Record<string, unknown>,
    onEvent: (event: StreamEvent) => void | Promise<void>,
  ): Promise<AnthropicStreamResult> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 180_000)
    const signal = plainOptions?.abortSignal ? mergeAbortSignals(controller.signal, plainOptions.abortSignal) : controller.signal
    try {
      const response = await devProxyFetch(messagesUrl, { method: 'POST', headers: requestHeaders, body: JSON.stringify(body), signal })
      if (!response.ok) {
        const detail = await response.text().catch(() => '')
        const error = new Error(`HTTP ${response.status}${detail ? `: ${detail}` : ''}`) as Error & { status: number }
        error.status = response.status
        throw error
      }
      const result = await readAnthropicStream(response, onEvent)
      const usage = result.usage
      logCacheUsage('Anthropic', String(body.model ?? model), usage
        ? {
            totalInputTokens: usage.input_tokens === undefined ? undefined : usage.input_tokens + (usage.cache_read_input_tokens ?? 0) + (usage.cache_creation_input_tokens ?? 0),
            cachedInputTokens: usage.cache_read_input_tokens,
          }
        : undefined)
      return result
    }
    catch (error) {
      logCacheUsage('Anthropic', String(body.model ?? model))
      throw error
    }
    finally {
      clearTimeout(timeoutId)
    }
  }

  // 主循环：支持多步工具调用
  let textStreamController!: ReadableStreamDefaultController<string>
  const textStream = new ReadableStream<string>({
    start(c) {
      textStreamController = c
    },
  })

  const onEvent = async (e: StreamEvent) => {
    try {
      textStreamController.enqueue(e.type === 'text-delta' ? e.text : '')
    }
    catch {}
    await plainOptions?.onStreamEvent?.(e)
  }

  const maxSteps = plainOptions?.maxSteps ?? 20
  const forceToolUse = Boolean(requireStructuredOutput
    && anthropicTools?.length
    && !(isAnthropicOfficialHost(baseURL) && isAnthropicForcedToolChoiceUnsupportedModel(String(chatConfig.model || model))))

  try {
    await runAnthropicToolLoop(anthropicMessages, {
      maxSteps,
      toolExecutors,
      terminalToolNames,
      lifecycleByName,
      requireToolCall: requireStructuredOutput,
      allowedToolNames: allowedStructuredTools,
      onToolBudgetExceeded: plainOptions?.chatToolContext?.onToolBudgetExceeded,
      onEvent,
      streamOnce: async (currentMessages) => {
        const body: Record<string, unknown> = {
          model: chatConfig.model || model,
          stream: true,
          ...(systemBlocks.length ? { system: systemBlocks } : {}),
          messages: currentMessages,
          ...(anthropicTools?.length ? { tools: anthropicTools } : {}),
          ...(forceToolUse ? { tool_choice: { type: 'any', disable_parallel_tool_use: true } } : {}),
          ...(metadataUserId ? { metadata: { user_id: metadataUserId } } : {}),
          ...getAnthropicThinkingConfig(String(chatConfig.model || model), thinkingLevel),
        }
        // 只重试请求和完整响应解析，工具副作用不属于网络重试范围。
        for (let attempt = 0; ; attempt++) {
          try {
            return await executeAnthropicStream(body, forwardStructuredEvents(onEvent, requireStructuredOutput, allowedStructuredTools))
          }
          catch (error) {
            if (attempt >= RETRY_CONFIG.maxRetries || plainOptions?.abortSignal?.aborted || !isRetryableError(error))
              throw error
            const delay = Math.min(RETRY_CONFIG.baseDelayMs * (2 ** attempt), RETRY_CONFIG.maxDelayMs)
            console.warn(`Anthropic 请求失败，${delay}ms 后重试 (${attempt + 1}/${RETRY_CONFIG.maxRetries})`)
            await new Promise(resolve => setTimeout(resolve, delay))
          }
        }
      },
    })
    return { textStream }
  }
  finally {
    try {
      textStreamController.close()
    }
    catch {}
  }
}

/**
 * Gemini 原生通道编排：装配工具 + 转换消息 → 委托 executeGeminiStream / runGeminiToolLoop。
 * 与 streamFromAnthropicNative 同构（工具装配、terminal 语义），但走 Gemini 原生 SSE 以拿到 thought。
 * 循环机制在 gemini-native-stream.ts（可离线单测），本函数只做 store 侧装配与 provider 配置。
 */
async function streamFromGeminiNativeChannel(
  model: string,
  chatProvider: ChatProvider,
  messages: Message[],
  options?: StreamOptions,
): Promise<{ textStream: ReadableStream<string> }> {
  const plainOptions = options ? toRaw(options) : undefined
  const { thinkingLevel } = useSettings()

  // 消息去响应式 + 过滤 error + 引用嵌入（与其它路径一致）
  const rawMessages = deepToRaw(
    messages.filter(msg => (msg.role as string) !== 'error').map((msg) => {
      const rawMsg = toRaw(msg)
      const role = rawMsg.role as string
      const content = embedQuotePrefix(rawMsg, contentForModel(rawMsg) as any)
      return {
        role,
        content,
        ...(role === 'system' && (rawMsg as any).metadata ? { metadata: (rawMsg as any).metadata } : {}),
      }
    }),
  )
  await compressLargeDataUrlsInMessages(rawMessages as Array<{ content?: unknown }>)

  const { contents, systemInstruction } = messagesToGeminiContents(rawMessages as Array<{ role: string, content: unknown }>)
  // 工具装配：chatActionTools（聊天动作）+ 用户 MCP，与 compat 路径一致
  const chatActionTools = plainOptions?.chatToolContext ? buildChatActionTools(plainOptions.chatToolContext) : []
  const requireStructuredOutput = !!plainOptions?.chatToolContext
  const terminalToolNames = getTerminalChatActionToolNames(chatActionTools)
  const userMcpTools = (await assembleMCPToolset()).tools
  const toolExecutors = new Map<string, (input: unknown, opts: unknown) => Promise<unknown>>()
  const lifecycleByName = new Map<string, ToolLifecycle>()
  // configure_mcp 常驻：与 Anthropic native 路径对齐，始终允许 AI 帮用户添加 MCP 服务器
  // tools=[] 语义：显式禁用所有工具，与 Anthropic native 保持一致
  const mergedTools = plainOptions?.tools !== undefined && plainOptions.tools.length === 0
    ? []
    : [...(plainOptions?.tools ?? []), ...userMcpTools, ...chatActionTools, configureMCPTool]
  const allowedStructuredTools = getStructuredToolAllowlist(mergedTools, plainOptions)
  const scopedTools = allowedStructuredTools
    ? filterToolsByStructuredAllowlist(mergedTools as Array<{ function?: { name?: string } }>, allowedStructuredTools)
    : mergedTools
  const openAIStyleTools = scopedTools.map((t) => {
    const raw = toRaw(t) as { function: { name: string, description?: string, parameters?: unknown }, execute?: (i: unknown, o: unknown) => Promise<unknown>, lifecycle?: 'query' | 'action' | 'terminal' }
    const lifecycle = resolveToolLifecycle(raw, terminalToolNames)
    lifecycleByName.set(raw.function.name, lifecycle)
    if (raw.execute) {
      registerToolExecutor(
        toolExecutors,
        raw.function.name,
        raw.execute,
        lifecycle === 'terminal',
        plainOptions?.chatToolContext?.onTerminalToolHandled,
        plainOptions?.chatToolContext?.onActionToolHandled,
        lifecycle,
      )
    }
    return { type: 'function', function: { name: raw.function.name, description: raw.function.description, parameters: raw.function.parameters } }
  })
  const geminiTools = toolsToGeminiDeclarations(openAIStyleTools)
  const chatConfig = deepToRaw(chatProvider.chat(model))
  const baseURL = String((chatConfig as { baseURL: string | URL }).baseURL).replace(/\/+$/, '').replace(/\/v1$/, '')
  const apiKey = String((chatConfig as { apiKey?: string }).apiKey ?? '')
  const realModel = (chatConfig as { model?: string }).model || model
  const url = `${baseURL}/v1beta/models/${realModel}:streamGenerateContent?alt=sse`
  // 合并用户自定义 headers（与 Anthropic 的 extraHeaders、Responses 的 headers 合并对称）；
  // x-goog-api-key 由 executeGeminiStream 内部固定注入，不被覆盖。
  const extraGeminiHeaders = plainOptions?.headers ? deepToRaw(plainOptions.headers) : undefined

  // Gemini 3 使用 REST 大写 thinkingLevel；2.5 使用 thinkingBudget；旧模型省略不支持的字段。
  // 第三方网关仍可能自行覆盖这些档位。
  const generationConfig = getGeminiThinkingConfig(realModel, thinkingLevel)

  let textStreamController!: ReadableStreamDefaultController<string>
  const textStream = new ReadableStream<string>({
    start(c) {
      textStreamController = c
    },
  })
  const onEvent = async (e: StreamEvent) => {
    try {
      textStreamController.enqueue(e.type === 'text-delta' ? e.text : '')
    }
    catch {}
    await plainOptions?.onStreamEvent?.(e)
  }

  const streamOnce = async (c: import('./gemini-native-stream').GeminiContent[]) => {
    const body: Record<string, unknown> = {
      contents: c,
      ...(systemInstruction ? { systemInstruction } : {}),
      ...(geminiTools ? { tools: [geminiTools] } : {}),
      ...(requireStructuredOutput && geminiTools
        ? {
            toolConfig: {
              functionCallingConfig: {
                mode: 'ANY',
                ...(allowedStructuredTools ? { allowedFunctionNames: [...allowedStructuredTools] } : {}),
              },
            },
          }
        : {}),
      ...(Object.keys(generationConfig).length ? { generationConfig } : {}),
    }
    return executeGeminiStream(url, apiKey, body, forwardStructuredEvents(onEvent, requireStructuredOutput, allowedStructuredTools), devProxyFetch, plainOptions?.abortSignal, extraGeminiHeaders, realModel)
  }

  try {
    await runGeminiToolLoop(contents, {
      streamOnce,
      toolExecutors,
      terminalToolNames,
      maxSteps: plainOptions?.maxSteps ?? 20,
      onEvent,
      requireToolCall: requireStructuredOutput,
      allowedToolNames: allowedStructuredTools,
      onToolBudgetExceeded: plainOptions?.chatToolContext?.onToolBudgetExceeded,
      actionToolNames: ACTION_TOOL_NAMES,
      lifecycleByName,
    })
  }
  finally {
    try {
      textStreamController.close()
    }
    catch {}
  }
  return { textStream }
}

/**
 * OpenAI Responses 原生通道编排：装配工具 + 转换消息 → 委托 executeResponsesStream / runResponsesToolLoop。
 * OpenAI 兼容模式吞掉 reasoning summary；Responses API + reasoning.summary='auto' 才吐可读思考。
 * 循环机制在 openai-responses-stream.ts（可离线单测），本函数只做 store 侧装配与 provider 配置。
 */
async function streamFromOpenAIResponsesChannel(
  model: string,
  chatProvider: ChatProvider,
  messages: Message[],
  options?: StreamOptions,
): Promise<{ textStream: ReadableStream<string> }> {
  const plainOptions = options ? toRaw(options) : undefined
  const { thinkingLevel } = useSettings()

  // 消息去响应式 + 过滤 error + 引用嵌入（与其它路径一致）
  const rawMessages = deepToRaw(
    messages.filter(msg => (msg.role as string) !== 'error').map((msg) => {
      const rawMsg = toRaw(msg)
      const role = rawMsg.role as string
      const content = embedQuotePrefix(rawMsg, contentForModel(rawMsg) as any)
      return {
        role,
        content,
        ...(role === 'system' && (rawMsg as any).metadata ? { metadata: (rawMsg as any).metadata } : {}),
      }
    }),
  )
  await compressLargeDataUrlsInMessages(rawMessages as Array<{ content?: unknown }>)

  const { instructions, input } = messagesToResponsesInput(rawMessages as Array<{ role: string, content: unknown }>)

  // 工具装配：chatActionTools（聊天动作）+ 用户 MCP，与 compat 路径一致
  const chatActionTools = plainOptions?.chatToolContext ? buildChatActionTools(plainOptions.chatToolContext) : []
  const requireStructuredOutput = !!plainOptions?.chatToolContext
  const terminalToolNames = getTerminalChatActionToolNames(chatActionTools)
  const userMcpTools = (await assembleMCPToolset()).tools
  const toolExecutors = new Map<string, (input: unknown, opts: unknown) => Promise<unknown>>()
  const lifecycleByName = new Map<string, ToolLifecycle>()
  // configure_mcp 常驻：与 Anthropic native 路径对齐，始终允许 AI 帮用户添加 MCP 服务器
  // tools=[] 语义：显式禁用所有工具，与 Anthropic native 保持一致
  const mergedTools = plainOptions?.tools !== undefined && plainOptions.tools.length === 0
    ? []
    : [...(plainOptions?.tools ?? []), ...userMcpTools, ...chatActionTools, configureMCPTool]
  const allowedStructuredTools = getStructuredToolAllowlist(mergedTools, plainOptions)
  const scopedTools = allowedStructuredTools
    ? filterToolsByStructuredAllowlist(mergedTools as Array<{ function?: { name?: string } }>, allowedStructuredTools)
    : mergedTools
  const openAIStyleTools = scopedTools.map((t) => {
    const raw = toRaw(t) as { function: { name: string, description?: string, parameters?: unknown }, execute?: (i: unknown, o: unknown) => Promise<unknown>, lifecycle?: 'query' | 'action' | 'terminal' }
    const lifecycle = resolveToolLifecycle(raw, terminalToolNames)
    lifecycleByName.set(raw.function.name, lifecycle)
    if (raw.execute) {
      registerToolExecutor(
        toolExecutors,
        raw.function.name,
        raw.execute,
        lifecycle === 'terminal',
        plainOptions?.chatToolContext?.onTerminalToolHandled,
        plainOptions?.chatToolContext?.onActionToolHandled,
        lifecycle,
      )
    }
    return { type: 'function' as const, function: { name: raw.function.name, description: raw.function.description, parameters: raw.function.parameters } }
  })
  const responsesTools = toolsToResponsesTools(openAIStyleTools)
  const chatConfig = deepToRaw(chatProvider.chat(model))
  const baseURL = String((chatConfig as { baseURL: string | URL }).baseURL).replace(/\/+$/, '')
  const apiKey = String((chatConfig as { apiKey?: string }).apiKey ?? '')
  const realModel = (chatConfig as { model?: string }).model || model
  const url = `${baseURL}/responses`
  // Keep Responses requests on a stable per-device cache bucket, matching the compatible path.
  const cacheUserId = await resolveCacheScope(apiKey, plainOptions?.cacheScopeId)
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...(apiKey ? { authorization: `Bearer ${apiKey}` } : {}),
    ...(plainOptions?.headers ? deepToRaw(plainOptions.headers) : {}),
  }

  // 默认档不发送 reasoning；显式档位再传 effort。
  const reasoning = getOpenAIResponsesReasoningConfig(thinkingLevel)

  let textStreamController!: ReadableStreamDefaultController<string>
  const textStream = new ReadableStream<string>({
    start(c) {
      textStreamController = c
    },
  })
  const onEvent = async (e: StreamEvent) => {
    try {
      textStreamController.enqueue(e.type === 'text-delta' ? e.text : '')
    }
    catch {}
    await plainOptions?.onStreamEvent?.(e)
  }

  const streamOnce = async (inp: unknown[]) => {
    const body: Record<string, unknown> = {
      model: realModel,
      input: inp,
      stream: true,
      ...(instructions ? { instructions } : {}),
      ...(responsesTools.length ? { tools: responsesTools } : {}),
      ...(requireStructuredOutput && responsesTools.length ? { tool_choice: 'required' } : {}),
      ...(reasoning ? { reasoning } : {}),
      ...(cacheUserId ? { prompt_cache_key: cacheUserId } : {}),
    }
    return executeResponsesStream(url, body, headers, forwardStructuredEvents(onEvent, requireStructuredOutput, allowedStructuredTools), devProxyFetch, plainOptions?.abortSignal)
  }

  try {
    await runResponsesToolLoop(input, {
      streamOnce,
      toolExecutors,
      terminalToolNames,
      maxSteps: plainOptions?.maxSteps ?? 20,
      onEvent,
      requireToolCall: requireStructuredOutput,
      allowedToolNames: allowedStructuredTools,
      onToolBudgetExceeded: plainOptions?.chatToolContext?.onToolBudgetExceeded,
      actionToolNames: ACTION_TOOL_NAMES,
      lifecycleByName,
    })
  }
  finally {
    try {
      textStreamController.close()
    }
    catch {}
  }
  return { textStream }
}

async function streamFrom(model: string, chatProvider: ChatProvider, messages: Message[], options?: StreamOptions) {
  // 🔧 安全检查：确保 chatProvider 存在
  if (!chatProvider) {
    throw new Error('streamFrom: chatProvider is undefined')
  }

  // 🔧 修复：将 options 对象也转换为普通对象
  const plainOptions = options ? toRaw(options) : undefined
  const headers = plainOptions?.headers

  // ⚠️ 安全关键：剥离 assistant 历史消息的展示专用字段（内心独白等，绝不回传模型）。
  // 回灌思考内容会污染上下文、浪费 token，且属 Claude 复读根因相邻风险区。
  // 幂等：fallback 递归再次调用本函数时重复剥离无副作用。
  messages = stripDisplayOnlyFields(messages)

  // 思考力度配置
  const { thinkingLevel } = useSettings()
  const thinkingConfig = getOpenAICompatibleReasoningConfig(model, thinkingLevel)

  // Claude 模型默认走原生 Messages 路径以支持 prompt caching（官方和兼容原生格式的第三方代理如 neko-teaparty 都受益）。
  // 若该 baseURL 曾在原生路径上返回 404/405，会被标记 1 小时，期间走 OpenAI 兼容路径。
  const chatConfigForCheck = deepToRaw(chatProvider.chat(model))
  const baseURLForCheck = String(chatConfigForCheck.baseURL).replace(/\/+$/, '')
  // 端点类型 override：读取 getProviderInstance 刻印在 chatProvider 实例上的 endpointKind
  // （不从 chat() 配置读——那会被 ...chatConfig spread 进 streamText body 泄漏未知字段）。
  // resolveEndpointKind：override='auto' 时按模型名推断（claude-*→anthropic-native / gemini-*→gemini-native / 其余→openai-compat），
  // 显式 override 则尊重之。三条原生分支全部以 resolvedKind 判定，
  // 确保 override='openai-compat' 能让 Claude/Gemini 模型跳过原生、override 也能强制启用原生。
  const providerEndpointKind = (chatProvider as { endpointKind?: EndpointKind }).endpointKind
  const resolvedKind = resolveEndpointKind(model, providerEndpointKind)
  const isAnthropicNative = resolvedKind === 'anthropic-native' && !isBaseUrlIncompatible(baseURLForCheck)

  if (isAnthropicNative) {
    try {
      return await streamFromAnthropicNative(model, chatProvider, messages, plainOptions)
    }
    catch (e) {
      // 只有明确的"端点不存在"错误才认为代理不支持原生格式；其它错误（鉴权、限流、网络、模型）直接上抛，不污染标记
      const msg = e instanceof Error ? e.message : String(e)
      const status = (e as { status?: number })?.status
      const isEndpointError
        = status === 404
          || status === 405
          || /\bHTTP\s+40[45]\b|method not allowed/i.test(msg)
      if (!isEndpointError) {
        throw e
      }
      console.warn(`[claude-caching] ${baseURLForCheck} 返回 404/405，标记 1 小时，fallback 到 OpenAI 兼容路径`)
      markBaseUrlAsIncompatible(baseURLForCheck)
      // 同一次请求内无缝重试：isBaseUrlIncompatible 此时已为 true，不会再进原生分支，不会死循环
      return await streamFrom(model, chatProvider, messages, options)
    }
  }

  // Gemini 原生通道：OpenAI 兼容模式吞掉 thought，必须走 streamGenerateContent + includeThoughts。
  // 404/405 = 端点不存在，静默 fallback；其余错误直接上抛（不记黑名单，不静默降级）。
  // resolvedKind/providerEndpointKind 已在 Anthropic block 前统一计算（见上）。
  if (resolvedKind === 'gemini-native') {
    try {
      return await streamFromGeminiNativeChannel(model, chatProvider, messages, plainOptions)
    }
    catch (e) {
      if (plainOptions?.abortSignal?.aborted || (e as Error)?.name === 'AbortError')
        throw e
      const status = (e as { status?: number })?.status
      if (status === 404 || status === 405) {
        console.warn(`[gemini-native] ${baseURLForCheck} 端点不存在 (HTTP ${status})，fallback 到 OpenAI 兼容路径（建议在设置中将端点类型改为 openai-compat）`)
        // 强制覆盖 endpointKind=openai-compat，防止 streamFrom 递归时再次进入 gemini-native 分支（无限递归）
        ;(chatProvider as { endpointKind?: EndpointKind }).endpointKind = 'openai-compat'
        return await streamFrom(model, chatProvider, messages, options)
      }
      throw e
    }
  }

  // OpenAI Responses 原生通道：兼容模式吞掉 reasoning summary，Responses API 才吐可读思考。
  // 同 Gemini：404/405 静默 fallback，其余错误直接上抛。
  if (resolvedKind === 'openai-responses') {
    try {
      return await streamFromOpenAIResponsesChannel(model, chatProvider, messages, plainOptions)
    }
    catch (e) {
      if (plainOptions?.abortSignal?.aborted || (e as Error)?.name === 'AbortError')
        throw e
      const status = (e as { status?: number })?.status
      if (status === 404 || status === 405) {
        console.warn(`[openai-responses] ${baseURLForCheck} 端点不存在 (HTTP ${status})，fallback 到 OpenAI 兼容路径（建议在设置中将端点类型改为 openai-compat）`)
        // 强制覆盖 endpointKind=openai-compat，防止 streamFrom 递归时再次进入 openai-responses 分支（无限递归）
        ;(chatProvider as { endpointKind?: EndpointKind }).endpointKind = 'openai-compat'
        return await streamFrom(model, chatProvider, messages, options)
      }
      throw e
    }
  }

  const claudeModel = isClaudeModel(model)

  // 非 Claude 模型：只保留 stable system 作为可复用前缀，半稳定/动态层附着到最新用户轮次。
  // 这样时间、记忆或关系变化不会改写第一条历史消息，OpenAI/Gemini 的前缀缓存可以继续复用。
  const normalizedMessages = moveNonStableSystemLayersToLatestUser(messages)

  const plainMessages = deepToRaw(
    normalizedMessages.filter(msg => (msg.role as string) !== 'error').map((msg) => {
      const rawMsg = toRaw(msg)
      const role = rawMsg.role

      // 将用户消息的 quote 字段嵌入 content，让 AI 知道用户在引用什么
      const content = embedQuotePrefix(rawMsg, contentForModel(rawMsg) as any) as Message['content']

      return {
        role,
        content,
        ...(role === 'system' && (rawMsg as any).metadata ? { metadata: (rawMsg as any).metadata } : {}),
      }
    }),
  )

  // 🔧 修复：深度移除响应式代理（保留函数）
  const chatConfig = deepToRaw(chatProvider.chat(model))
  const plainHeaders = claudeModel
    ? getClaudeHeaders(headers ? deepToRaw(headers) : undefined)
    : (headers ? deepToRaw(headers) : undefined)

  // OpenAI 兼容路径下的用户/设备隔离：
  // 多人共享同一 apiKey 时，prompt_cache_key 强制按设备分桶；safety_identifier 让 OpenAI 的滥用检测/限流按真实用户粒度统计。
  // 走到这里就一定是 OpenAI 兼容协议（Claude 原生路径已在上方 return），所以无需按模型名再 gate。
  // 不发 legacy 的 `user` 字段：部分第三方代理把 body.user 当作鉴权或路由 tag，可能导致 400/403。
  const cacheApiKey = String((chatConfig as any).apiKey ?? '')
  const cacheUserId = await resolveCacheScope(cacheApiKey, plainOptions?.cacheScopeId)

  // 获取工具列表
  // 优先使用调用方传入的 tools（插件工作台等自定义工具链），否则回落到默认 mcp+debug
  // [] = 显式禁用，undefined = 走下面的默认链
  let toolsList: unknown[] | undefined
  // MCP 始终按元工具注入（list/load/call/remove），不把服务器真实工具全量塞进上下文
  const userMcpTools = (await assembleMCPToolset()).tools
  if (plainOptions?.tools !== undefined) {
    // [] = 显式禁用所有工具（含 configure_mcp）
    const base = plainOptions.tools.length > 0
      ? [...plainOptions.tools, configureMCPTool, ...userMcpTools]
      : []
    toolsList = base.length > 0 ? base : undefined
  }
  else {
    const toolsOk = streamOptionsToolsCompatibilityOk(model, chatProvider, messages, plainOptions, plainOptions?.toolsCompatibility)
    // ⭐ compat（OpenAI 兼容 / 非 Claude）路径工具注入说明：
    // chatActionTools（send_messages 等）仅在 chatToolContext 分支（下方约100行）注入，
    // 在那里会与 plainTools 合并后通过 executeOpenAICompatStream 发送给模型。
    // 正常聊天链路已经具备手动工具循环。兼容性探测尚未完成时也应注入 MCP，
    // 否则首轮请求会静默失去 MCP；只有缓存明确判定不支持时才跳过。
    const compatibilityKey = `${chatProvider.chat(model).baseURL}-${model}`
    const explicitlyUnsupported = plainOptions?.toolsCompatibility?.get(compatibilityKey) === false
    const enableTools = userMcpTools.length > 0
      && !explicitlyUnsupported
      && (toolsOk || !!plainOptions?.chatToolContext)
    toolsList = enableTools
      ? [...await mcp(), ...await debug(), ...userMcpTools, configureMCPTool]
      : undefined
  }

  // 🔧 修复：深度移除响应式代理（保留 execute 函数）
  const plainTools = toolsList ? deepToRaw(toolsList) : undefined

  // 🔧 自定义 fetch：立即检测 HTTP 错误
  // 策略：
  //   - 可重试错误（503/429/502）→ throw，让外层重试循环捕获并重试
  //   - 不可重试错误（401/403 等）→ 通过 onStreamEvent 发射 error 事件
  //     （因为 xsai 的 streamText 不传播 fetch 错误，throw 会变成 Uncaught in promise）
  const errorCheckingFetch: typeof globalThis.fetch = async (input, init) => {
    let compatibleModel = model
    if (typeof init?.body === 'string') {
      try {
        const requestModel = JSON.parse(init.body)?.model
        if (typeof requestModel === 'string')
          compatibleModel = requestModel
      }
      catch {}
    }
    const compatibleProvider = isClaudeModel(compatibleModel)
      ? 'Claude'
      : compatibleModel.toLowerCase().includes('gemini')
        ? 'Gemini'
        : 'OpenAI-compatible'

    // 🔧 流式首字节超时：等待上游模型开始响应（思考模型可能需要较长时间）
    // fetch() 在收到 HTTP 200 响应头时 resolve，之后 clearTimeout，不再限制 chunk 间隔
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 180_000)
    const mergedInit = {
      ...init,
      signal: init?.signal
        ? mergeAbortSignals(controller.signal, init.signal)
        : controller.signal,
    }
    let response: Response
    try {
      response = await devProxyFetch(input, mergedInit)
    }
    catch (e) {
      clearTimeout(timeoutId)
      logCacheUsage(compatibleProvider, compatibleModel)
      throw e
    }
    clearTimeout(timeoutId)
    if (!response.ok) {
      logCacheUsage(compatibleProvider, compatibleModel)
      const body = await response.text().catch(() => '')
      console.error(`📥 LLM 响应错误: HTTP ${response.status}`)
      const error = new Error(`HTTP ${response.status}${body ? `: ${body}` : ''}`)
      // 可重试错误：直接 throw，让重试循环的 catch 捕获
      if (isRetryableError(error)) {
        throw error
      }
      // 不可重试错误：通过事件回调通知上层，返回空响应让 streamText 安静结束
      await plainOptions?.onStreamEvent?.({ type: 'error', error })
      return new Response('', { status: 200, headers: { 'content-type': 'text/event-stream' } })
    }

    // 🔧 修复格式异常的 SSE 流：某些代理会在每个 chunk 后插入 finish_reason:"stop"
    // 导致 xsAI 在真正内容到达前就结束流处理。
    // 策略：缓冲所有 SSE 行，遇到 finish_reason:"stop" 时先暂存，
    // 若后续还有内容 chunk 则丢弃该假 stop，只放行最后一个真正的 stop。
    if (response.body) {
      const originalBody = response.body
      const encoder = new TextEncoder()
      const decoder = new TextDecoder()
      let buffer = ''
      let pendingStopLine = ''
      let lastUsage: unknown
      const captureUsage = (line: string) => {
        if (!line.startsWith('data: ') || line === 'data: [DONE]')
          return
        try {
          lastUsage = JSON.parse(line.slice(6).trim())?.usage ?? lastUsage
        }
        catch {}
      }

      const fixTransform = new TransformStream<Uint8Array, Uint8Array>({
        transform(chunk, controller) {
          const text = decoder.decode(chunk, { stream: true })
          buffer += text

          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''

          for (const rawLine of lines) {
            const line = rawLine.replace(/\r$/, '')
            captureUsage(line)

            if (line.startsWith('data: ') && line !== 'data: [DONE]') {
              if (shouldBufferSseStopLine(line)) {
                pendingStopLine = `${line}\n`
                continue
              }

              if (pendingStopLine && line.includes('"content"'))
                pendingStopLine = ''
            }
            else if (line === 'data: [DONE]') {
              if (pendingStopLine)
                controller.enqueue(encoder.encode(pendingStopLine))
              pendingStopLine = ''
              controller.enqueue(encoder.encode(`${line}\n`))
              continue
            }

            controller.enqueue(encoder.encode(`${line}\n`))
          }
        },
        flush(controller) {
          const decoderTail = decoder.decode()
          buffer += decoderTail
          captureUsage(buffer.trim())
          if (pendingStopLine)
            controller.enqueue(encoder.encode(pendingStopLine))
          if (buffer)
            controller.enqueue(encoder.encode(buffer))
          logCacheUsage(compatibleProvider, compatibleModel, normalizeCompatibleCacheUsage(lastUsage))
        },
      })

      return new Response(originalBody.pipeThrough(fixTransform), {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      })
    }

    logCacheUsage(compatibleProvider, compatibleModel)
    return response
  }

  // ⭐ 双通道：兼容路径也支持工具通道——仅当聊天主链路传入 chatToolContext 时启用手动循环。
  // 已明确判定不支持工具的模型不进入此分支，避免把 chat actions 发给必然拒绝工具的端点；
  // 兼容性未知时仍照常尝试，避免首次聊天丢失工具能力。
  const compatToolsUnsupported = plainOptions?.toolsCompatibility?.get(`${chatProvider.chat(model).baseURL}-${model}`) === false
  const requireStructuredOutput = !!plainOptions?.chatToolContext
  if (plainOptions?.chatToolContext && !compatToolsUnsupported) {
    const chatActionTools = buildChatActionTools(plainOptions!.chatToolContext!)
    const terminalToolNames = getTerminalChatActionToolNames(chatActionTools)
    const toolExecutors = new Map<string, (input: unknown, opts: unknown) => Promise<unknown>>()
    const lifecycleByName = new Map<string, ToolLifecycle>()
    // 去重：plainTools 里可能已含 configureMCPTool（MCP 服务器启用时），避免发给 API 重名工具定义
    const seenToolNames = new Set<string>()
    const compatToolDefinitions = [...(plainTools ?? []), ...chatActionTools, configureMCPTool]
      .filter((t) => {
        const name = (toRaw(t) as { function?: { name?: string } })?.function?.name
        if (!name || seenToolNames.has(name))
          return false
        seenToolNames.add(name)
        return true
      })
    const allowedStructuredTools = getStructuredToolAllowlist(compatToolDefinitions, plainOptions)
    const scopedCompatToolDefinitions = allowedStructuredTools
      ? filterToolsByStructuredAllowlist(compatToolDefinitions as Array<{ function?: { name?: string } }>, allowedStructuredTools)
      : compatToolDefinitions
    const compatTools = scopedCompatToolDefinitions
      .map((t) => {
        const raw = toRaw(t) as { function: { name: string, description?: string, parameters?: unknown }, execute?: (i: unknown, o: unknown) => Promise<unknown>, lifecycle?: 'query' | 'action' | 'terminal' }
        const lifecycle = resolveToolLifecycle(raw, terminalToolNames)
        lifecycleByName.set(raw.function.name, lifecycle)
        if (raw.execute) {
          registerToolExecutor(
            toolExecutors,
            raw.function.name,
            raw.execute,
            lifecycle === 'terminal',
            plainOptions?.chatToolContext?.onTerminalToolHandled,
            plainOptions?.chatToolContext?.onActionToolHandled,
            lifecycle,
          )
        }
        return { type: 'function', function: { name: raw.function.name, description: raw.function.description, parameters: raw.function.parameters } }
      })
    const compatBaseURL = String((chatConfig as { baseURL: string | URL }).baseURL).replace(/\/+$/, '')
    const compatUrl = `${compatBaseURL}/chat/completions`
    const compatApiKey = String((chatConfig as { apiKey?: string }).apiKey ?? '')
    const compatHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(compatApiKey ? { Authorization: `Bearer ${compatApiKey}` } : {}),
      ...(plainHeaders ?? {}),
    }

    let compatTextStreamController!: ReadableStreamDefaultController<string>
    const compatTextStream = new ReadableStream<string>({
      start(c) {
        compatTextStreamController = c
      },
    })
    const compatOnEvent = async (e: StreamEvent) => {
      try {
        compatTextStreamController.enqueue(e.type === 'text-delta' ? e.text : '')
      }
      catch {}
      await plainOptions?.onStreamEvent?.(e)
    }

    const streamOnce = async (msgs: unknown[]): Promise<CompatStreamResult> => {
      let lastErr: unknown
      for (let attempt = 0; attempt <= RETRY_CONFIG.maxRetries; attempt++) {
        try {
          const body: Record<string, unknown> = {
            model: (chatConfig as { model?: string }).model || model,
            stream: true,
            messages: msgs,
            ...(compatTools.length ? { tools: compatTools } : {}),
            ...(requireStructuredOutput && compatTools.length ? { tool_choice: 'required' } : {}),
            ...(cacheUserId ? { prompt_cache_key: cacheUserId, safety_identifier: cacheUserId } : {}),
            ...thinkingConfig,
          }
          return await executeOpenAICompatStream(compatUrl, body, compatHeaders, forwardStructuredEvents(compatOnEvent, requireStructuredOutput, allowedStructuredTools), errorCheckingFetch, plainOptions?.abortSignal)
        }
        catch (error) {
          lastErr = error
          if (attempt < RETRY_CONFIG.maxRetries && isRetryableError(error)) {
            const delay = Math.min(RETRY_CONFIG.baseDelayMs * (2 ** attempt), RETRY_CONFIG.maxDelayMs)
            console.warn(`⚠️ LLM 兼容路径请求失败 (${String(error).slice(0, 80)}), ${delay}ms 后重试 (${attempt + 1}/${RETRY_CONFIG.maxRetries})`)
            await new Promise(r => setTimeout(r, delay))
            continue
          }
          throw error
        }
      }
      throw lastErr
    }

    try {
      await runCompatToolLoop(plainMessages, { streamOnce, toolExecutors, terminalToolNames, maxSteps: plainOptions?.maxSteps ?? 20, onEvent: compatOnEvent, onToolBudgetExceeded: plainOptions?.chatToolContext?.onToolBudgetExceeded, actionToolNames: ACTION_TOOL_NAMES, lifecycleByName, requireToolCall: requireStructuredOutput, allowedToolNames: allowedStructuredTools })
    }
    finally {
      try {
        compatTextStreamController.close()
      }
      catch {}
    }
    return { textStream: compatTextStream }
  }

  // 🔧 带重试的请求
  let lastError: unknown
  // 为兼容路径合成 tool-call-streaming-start：xsAI 不提供该事件，这里按 toolCallId 去重，
  // 在同一 toolCallId 首次出现（delta 或最终 tool-call）时前置发射一次，行为与原生路径对齐
  const emittedStreamingStart = new Set<string>()
  const maybeEmitStreamingStart = async (event: any) => {
    if (!plainOptions?.onStreamEvent)
      return
    const type = event?.type
    if (type !== 'tool-call-delta' && type !== 'tool-call')
      return
    const id = event?.toolCallId
    const name = event?.toolName
    if (!id || emittedStreamingStart.has(id))
      return
    emittedStreamingStart.add(id)
    await plainOptions.onStreamEvent({ type: 'tool-call-streaming-start', toolCallId: id, toolName: name || 'unknown' })
  }

  for (let attempt = 0; attempt <= RETRY_CONFIG.maxRetries; attempt++) {
    try {
      return await streamText({
        ...chatConfig,
        ...thinkingConfig,
        fetch: errorCheckingFetch,
        maxSteps: plainOptions?.maxSteps ?? 20,
        messages: plainMessages as Message[],
        headers: plainHeaders,
        tools: plainTools as Parameters<typeof streamText>[0]['tools'],
        // 共享 apiKey 场景下的缓存/滥用检测分桶字段（OpenAI 系识别，多数第三方代理忽略未知字段）
        ...(typeof cacheUserId === 'string' && cacheUserId.length > 0
          ? {
              prompt_cache_key: cacheUserId,
              safety_identifier: cacheUserId,
            }
          : {}),
        ...(plainOptions?.abortSignal ? { abortSignal: plainOptions.abortSignal } : {}),
        async onEvent(event) {
          await maybeEmitStreamingStart(event)
          await plainOptions?.onStreamEvent?.(event as StreamEvent)
        },
      })
    }
    catch (error) {
      lastError = error
      if (attempt < RETRY_CONFIG.maxRetries && isRetryableError(error)) {
        const delay = Math.min(
          RETRY_CONFIG.baseDelayMs * (2 ** attempt),
          RETRY_CONFIG.maxDelayMs,
        )
        console.warn(`⚠️ LLM 请求失败 (${String(error).slice(0, 80)}), ${delay}ms 后重试 (${attempt + 1}/${RETRY_CONFIG.maxRetries})`)
        await new Promise(r => setTimeout(r, delay))
        continue
      }
      throw error
    }
  }
  throw lastError
}

export async function attemptForToolsCompatibilityDiscovery(model: string, chatProvider: ChatProvider, _: Message[], options?: Omit<StreamOptions, 'supportsTools'>): Promise<boolean> {
  const probeTool = {
    function: {
      name: '__meowchat_tools_probe',
      description: '内部兼容性探测工具。不要在用户可见回复中提及。',
      parameters: { type: 'object', properties: {} },
    },
    execute: async () => '',
  }

  async function attempt(enable: boolean) {
    try {
      let streamError: unknown
      const res = await streamFrom(model, chatProvider, [{ role: 'user', content: 'Hello, world!' }], {
        ...options,
        supportsTools: enable,
        tools: enable ? [probeTool] : [],
        onStreamEvent: async (event) => {
          if (event.type === 'error')
            streamError = event.error
          await options?.onStreamEvent?.(event)
        },
      })
      for await (const _ of readableStreamToAsyncIterator(res.textStream)) {
        // Drop
      }

      if (streamError)
        throw streamError

      return true
    }
    catch (err) {
      const message = String(err)
      // 常见网关/本地模型对工具能力的拒绝，不应让兼容性探测整轮失败。
      if (/does not support (?:tools?|functions?)|no endpoints found that support tool use|unsupported.*(?:tool|function)|(?:tool|function).*(?:not supported|not available)/i.test(message)) {
        return false
      }
      throw err
    }
  }

  function promiseAllWithInterval<T>(promises: (() => Promise<T>)[], interval: number): Promise<{ result?: T, error?: any }[]> {
    return new Promise((resolve) => {
      const results: { result?: T, error?: any }[] = []
      let completed = 0

      promises.forEach((promiseFn, index) => {
        setTimeout(() => {
          promiseFn()
            .then((result) => {
              results[index] = { result }
            })
            .catch((err) => {
              results[index] = { error: err }
            })
            .finally(() => {
              completed++
              if (completed === promises.length) {
                resolve(results)
              }
            })
        }, index * interval)
      })
    })
  }

  const attempts = [
    () => attempt(true),
    () => attempt(false),
  ]

  const attemptsResults = await promiseAllWithInterval<boolean | undefined>(attempts, 1000)
  if (attemptsResults.some(res => res.error)) {
    const err = new Error(`Error during tools compatibility discovery for model: ${model}. Errors: ${attemptsResults.map(res => res.error).filter(Boolean).join(', ')}`)
    err.cause = attemptsResults.map(res => res.error).filter(Boolean)
    throw err
  }

  return attemptsResults[0].result === true && attemptsResults[1].result === true
}

export const useLLM = defineStore('llm', () => {
  const toolsCompatibility = ref<Map<string, boolean>>(new Map())

  async function discoverToolsCompatibility(model: string, chatProvider: ChatProvider, _: Message[], options?: Omit<StreamOptions, 'supportsTools'>) {
    // 🔧 安全检查：确保 chatProvider 存在
    if (!chatProvider) {
      console.warn('⚠️ discoverToolsCompatibility: chatProvider is undefined')
      return
    }

    // Cached, no need to discover again
    if (toolsCompatibility.value.has(`${chatProvider.chat(model).baseURL}-${model}`)) {
      return
    }

    const res = await attemptForToolsCompatibilityDiscovery(model, chatProvider, _, { ...options, toolsCompatibility: toolsCompatibility.value })
    toolsCompatibility.value.set(`${chatProvider.chat(model).baseURL}-${model}`, res)
  }

  function stream(model: string, chatProvider: ChatProvider, messages: Message[], options?: StreamOptions) {
    // 🔧 安全检查：确保 chatProvider 存在
    if (!chatProvider) {
      throw new Error('stream: chatProvider is undefined')
    }

    return streamFrom(model, chatProvider, messages, { ...options, toolsCompatibility: toolsCompatibility.value })
  }

  async function models(apiUrl: string, apiKey: string) {
    if (apiUrl === '') {
      return []
    }

    try {
      return await listModels({
        baseURL: (apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`) as `${string}/`,
        apiKey,
        fetch: devProxyFetch,
      })
    }
    catch (err) {
      if (String(err).includes(`Failed to construct 'URL': Invalid URL`)) {
        return []
      }

      throw err
    }
  }

  return {
    models,
    stream,
    discoverToolsCompatibility,
  }
})
